import { useImageFallback } from "../useImageFallback";
import { ProductMediaPlaceholder } from "./ProductMediaPlaceholder";

export interface ProductCardMediaProps {
  src: string | undefined;
  /** Nombre del producto, para el texto alternativo. */
  name: string;
}

/**
 * La imagen de la tarjeta, en proporción 3:4.
 *
 * ── Por qué la proporción es fija ────────────────────────────────────
 * El contenedor reserva su alto ANTES de que la imagen cargue
 * (`aspect-[3/4]` + `absolute inset-0`). Sin eso, la rejilla salta cuando
 * llegan las fotos y quien iba a pulsar una tarjeta acaba pulsando otra.
 *
 * ── El acercamiento ──────────────────────────────────────────────────
 * 1.08 en 900 ms, los mismos números que la tienda de producción: el
 * acercamiento lento es la mitad de su firma visual —la otra mitad es el
 * botón que sube—, y a 300 ms se siente como otro sitio. `overflow-hidden`
 * del padre es lo que recorta el exceso.
 *
 * ── Cuando no hay foto, o la hay y no carga ──────────────────────────
 * Las dos situaciones acaban en el mismo marcador "Próximamente", y esa
 * decisión ya no vive aquí: `useImageFallback` la resuelve y
 * `ProductMediaPlaceholder` la pinta, porque la galería de la ficha y la
 * miniatura del carrito necesitan exactamente lo mismo. Tres copias de la
 * misma regla significaría que el día que cambie el marcador cambie en
 * una sola.
 */
export function ProductCardMedia({ src, name }: ProductCardMediaProps) {
  const { showImage, onError } = useImageFallback(src);

  return (
    <div className="relative overflow-hidden rounded-ds-xl bg-ds-mist/50">
      <div className="aspect-[3/4] w-full" />

      {showImage ? (
        <img
          src={src}
          alt={name}
          loading="lazy"
          onError={onError}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08] motion-reduce:transform-none"
        />
      ) : (
        <ProductMediaPlaceholder size="md" />
      )}

      {/*
        ── El velo, y por qué no es el de producción ──────────────────
        La tienda actual pinta `from-black/50 via-black/5` al 60% de
        opacidad, que sube al 80% con el ratón encima. Sobre una
        fotografía CLARA eso deja el título blanco en 2.1:1 — la mitad
        del mínimo legible. Y no puede arreglarse con el ratón: un texto
        no se vuelve legible solo al señalarlo.

        Este velo es más OSCURO abajo y más CORTO arriba. Las paradas no
        son decorativas: al 30% de la altura sigue habiendo tinta al 65%,
        que sobre el peor fondo posible —blanco puro— deja el texto
        blanco en 5.2:1; por encima del 30% no hay texto ni siquiera con
        el botón desplegado, así que ahí el velo se apaga y la fotografía
        se ve. Con la parada por defecto (transparente al 100%) el gris
        subía hasta el borde superior y apagaba la foto entera.

        El oscurecimiento del original SÍ se conserva, pero como una
        SEGUNDA capa que se suma encima. Así se mantiene el gesto —la
        foto se apaga un punto cuando la tarjeta despierta— sin que la
        legibilidad dependa de él.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-ds-ink/90 via-ds-ink/65 via-30% to-transparent to-70%"
      />

      <div
        aria-hidden="true"
        className="absolute inset-0 bg-ds-ink/0 transition-colors duration-500 group-hover:bg-ds-ink/10 group-focus-within:bg-ds-ink/10"
      />
    </div>
  );
}
